import { ChangeEvent, useEffect } from "react"
import { searchCustomer } from "src/apis/customerApi"
import { CodeForm } from "src/components/common/form/CodeForm"
import { DropDownForm } from "src/components/common/form/DropdownForm"
import { ImageForm } from "src/components/common/form/ImageForm"
import { InputForm } from "src/components/common/form/input/InputForm"
import { useInfoFormEvents } from "src/hooks/useInfoFormEvents"
import { MST4050_R01HeaderDto } from "./Dto_MST4050_R01"
import { useCommonCodeStore } from "src/store/useCommonCodeStore"
import useUiuxStore from "src/store/useUiuxStore"
import { InfoCodeSearchFieldConfig } from "src/types/info-form"
import { useMST4050_R01Store } from "./Store_MST4050_R01"

interface Props {
	data?: MST4050_R01HeaderDto
	defaultMST4050_R01HeaderRow: MST4050_R01HeaderDto
}

// 선택 또는 신규 의약품의 기본정보를 입력하고 수정하는 폼을 렌더링한다.
export const Info_MST4050_R01 = ({ data, defaultMST4050_R01HeaderRow }: Props) => {

	const {
		selectedMST4050_R01,
		editMST4050_R01,
		setEditMST4050_R01,
	} = useMST4050_R01Store();
	const {
		chemicalTypeList,
		eqmStatusList,
		compCdList,
		orgCdList,
		unitList,
	} = useCommonCodeStore();
	const { setCodeSearchModalProps } = useUiuxStore();

	// 단위가 비어 있으면 드롭다운에 표시되는 첫 옵션을 실제 편집 상태에도 반영한다.
	useEffect(() => {
		const defaultUnit = unitList[0]?.minorCd;
		if (editMST4050_R01.eqmUnit || !defaultUnit) return;
		setEditMST4050_R01({ ...editMST4050_R01, eqmUnit: defaultUnit });
	}, [
		editMST4050_R01,
		setEditMST4050_R01,
		unitList,
	]);

	// 매입처 코드 입력 및 팝업 선택 후 코드/거래처명을 화면 DTO에 연결한다.
	const useCustomerFocusId = 'useCustomerInput';
	const infoFieldConfigs: InfoCodeSearchFieldConfig[] = [
		{
			fieldName: 'useCustomer',
			category: '거래처',
			focusId: useCustomerFocusId,
			responseMapping: {
				customCd: 'useCustomer',
				customNm: 'useCustomerNm',
			},
			searchFunction: async (code: string) => {
				const customerResponse = await searchCustomer(code);
				return customerResponse.data.data.find(value => value.customCd === code);
			},
			extraFilter: () => ({
				customCd: editMST4050_R01.useCustomer || '',
				customNm: editMST4050_R01.useCustomerNm || '',
			}),
		},
	];

	// 공통 Info 이벤트 훅에서 직접 입력, Enter 검색, 더블클릭 팝업 핸들러를 생성한다.
	const { getFieldHandlers } = useInfoFormEvents({
		editData: editMST4050_R01,
		setEditData: setEditMST4050_R01,
		defaultData: defaultMST4050_R01HeaderRow,
		infoFieldConfigs,
		setCodeSearchModalProps,
		formName: 'MST4050_R01',
	});

	// 일반 입력 및 드롭다운 변경 값을 편집 중인 헤더 데이터에 반영한다.
	const handleChange = (e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) => {
		const { name, value } = e.target;
		setEditMST4050_R01({
			...editMST4050_R01,
			[name]: value
		});
	}

	// 사진 영역은 공통 기본 크기보다 크게 표시해 우측 40% 영역을 활용한다.
	const eqmImageWrapperStyle = {
		wrapperStyle: "w-[300px] h-[250px]",
	}

	return(
		<div className="w-full h-full overflow-y-auto flex border border-gray-300">
			<div className={`
				w-[60%]
				h-full flex flex-wrap content-start p-[12px]`}>

				<InputForm
					title="약품코드"
					value={editMST4050_R01.equipmentCd}
					editable={selectedMST4050_R01 === undefined}
					name="equipmentCd"
					onChange={handleChange}
				/>
				<InputForm
					title="약품명"
					value={editMST4050_R01.equipmentNm}
					editable={true}
					name="equipmentNm"
					onChange={handleChange}
				/>
				<DropDownForm
					title="약품유형"
					value={editMST4050_R01.equipmentType}
					data={chemicalTypeList}
					editable={true}
					name="equipmentType"
					initLabelLess={true}
					onChange={handleChange}
				/>
				<DropDownForm
					title="단위"
					value={editMST4050_R01.eqmUnit}
					data={unitList}
					editable={true}
					name="eqmUnit"
					initLabelLess={true}
					onChange={handleChange}
				/>
				<DropDownForm
					title="사업장"
					value={editMST4050_R01.compCd}
					data={compCdList}
					editable={true}
					name="compCd"
					initLabelLess={true}
					onChange={handleChange}
				/>
				<DropDownForm
					title="관리부서"
					value={editMST4050_R01.eqmOrgcd}
					data={orgCdList}
					editable={true}
					name="eqmOrgcd"
					initLabelLess={true}
					onChange={handleChange}
				/>
				<CodeForm
					label="매입처"
					value={editMST4050_R01.useCustomer || ""}
					custNmValue={editMST4050_R01.useCustomerNm || ""}
					editable={true}
					handleFoucsId={useCustomerFocusId}
					name="useCustomer"
					handleChange={getFieldHandlers('useCustomer').onChange}
					onDoubleClick={getFieldHandlers('useCustomer').onDoubleClick}
					onKeyDown={getFieldHandlers('useCustomer').onEnterKeyDown}
				/>
				<InputForm
					title="CAS번호"
					value={editMST4050_R01.eqmSerial}
					editable={true}
					name="eqmSerial"
					onChange={handleChange}
				/>
				<DropDownForm
					title="상태"
					value={editMST4050_R01.eqmStatus}
					data={eqmStatusList}
					editable={true}
					name="eqmStatus"
					initLabelLess={true}
					onChange={handleChange}
				/>
				<InputForm
					title="보유량"
					value={(editMST4050_R01.eqmQty ?? 0).toLocaleString()}
					editable={false}
					name="eqmQty"
					onChange={handleChange}
				/>
				<InputForm
					title="비고"
					value={editMST4050_R01.remark}
					editable={true}
					name="remark"
					onChange={handleChange}
					wrapperOption={{
						wrapperStyle: "w-[510px] min-w-[510px] mr-[10px] mb-[5px]",
						titleStyle: "w-[100px] min-w-[100px]",
						inputStyle: "w-[510px] min-w-[510px]",
					}}
				/>
			</div>
			<div className="w-[40%] h-full flex flex-wrap gap-[12px] pt-[12px]">
				<ImageForm
					title="사진"
					image={editMST4050_R01.eqmImage}
					onImageChange={(eqmImage) => setEditMST4050_R01({ ...editMST4050_R01, eqmImage })}
					wrapperOption={eqmImageWrapperStyle}
				/>
			</div>
		</div>
	)
}
